import { useTranslation } from 'react-i18next';
import { capitalizeSign } from '../me/chartViewAdapter';
import styles from './AspectsTable.module.css';

export interface AspectsTableProps {
  sun: string | null | undefined;
  moon: string | null | undefined;
  rising: string | null | undefined;
  dominant: string | null | undefined;
  /** Black Moon Lilith sign. Absent on legacy charts → the row is skipped. */
  lilith?: string | null | undefined;
}

const SIGN_GLYPH: Record<string, string> = {
  aries: '♈', taurus: '♉', gemini: '♊', cancer: '♋',
  leo: '♌', virgo: '♍', libra: '♎', scorpio: '♏',
  sagittarius: '♐', capricorn: '♑', aquarius: '♒', pisces: '♓',
};

function glyphFor(sign: string | null | undefined): string {
  if (!sign) return '';
  return SIGN_GLYPH[sign.trim().toLowerCase()] ?? '';
}

type RowKey = 'sun' | 'moon' | 'rising' | 'lilith' | 'dominant';

interface Row {
  sym: string;
  key: RowKey;
  labelKey: string;
  val: string | null | undefined;
  glyph: string;
}

export function AspectsTable({ sun, moon, rising, dominant, lilith }: AspectsTableProps): JSX.Element {
  const { t } = useTranslation('flows');
  // The server sends lowercase signs («leo»); the sign-name keys are capitalised («Leo» → «Лев»).
  const sign = (v: string | null | undefined): string | null | undefined => {
    if (!v) return v;
    const cap = capitalizeSign(v.trim().toLowerCase());
    return t(`me:natalChart.names.signs.${cap}`, { defaultValue: cap });
  };
  const rows: Row[] = [
    { sym: '☉',  key: 'sun',      labelKey: 'onboarding.reveal.aspects.sunLabel',      val: sign(sun),      glyph: glyphFor(sun) },
    { sym: '☾',  key: 'moon',     labelKey: 'onboarding.reveal.aspects.moonLabel',     val: sign(moon),     glyph: glyphFor(moon) },
    { sym: 'AC', key: 'rising',   labelKey: 'onboarding.reveal.aspects.risingLabel',   val: sign(rising),   glyph: glyphFor(rising) },
    // Black Moon only when the chart actually carries it — legacy charts don't,
    // and an empty "…" row would read as a missing value rather than an absent point.
    ...(lilith
      ? [{ sym: '⚸', key: 'lilith' as const, labelKey: 'onboarding.reveal.aspects.lilithLabel', val: sign(lilith), glyph: glyphFor(lilith) }]
      : []),
    { sym: '♀',  key: 'dominant', labelKey: 'onboarding.reveal.aspects.dominantLabel', val: dominant, glyph: '' },
  ];

  return (
    <div
      className={styles.table}
      role="table"
      aria-label={t('onboarding.reveal.aspects.tableAria')}
    >
      {rows.map((r) => (
        <div key={r.key} className={styles.row} role="row">
          <span className={styles.sym} aria-hidden="true">{r.sym}</span>
          <span className={styles.label}>{t(r.labelKey)}</span>
          <span className={styles.value}>{r.val ?? '…'}</span>
          <span className={styles.glyph} aria-hidden="true">{r.glyph}</span>
        </div>
      ))}
    </div>
  );
}
